import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useApiClient } from '@/composables/use-api-client.ts';
import { useSessionStateStore } from '@/stores/session-state.ts';

export interface RoundData {
    id: string;
    order: number;
    result?: string;
    title?: string;
    description?: string;
    externalLink?: string;
    replays: number;
    lastReplayVotes: number;
    finalVote?: number | string;
}

export const useRoundsStore = defineStore('rounds', () => {
    const { execute, data, isLoading } = useApiClient<RoundData[]>();
    const { execute: updateRoundVote, isLoading: isLoadingRoundVoteUpdate } = useApiClient();
    const rounds = ref<RoundData[]>([]);
    const roundsSearchResults = ref<RoundData[]>([]);

    async function fetch() {
        const sessionState = useSessionStateStore();
        const sessionId = sessionState.sessionId;
        if (sessionId && !isLoading.value) {
            await execute(`/session/${sessionId}/rounds`);
            rounds.value = data.value ? data.value : ([] as RoundData[]);
        }

        return rounds.value;
    }

    async function setVote(roundId: string, vote: string | number) {
        const sessionState = useSessionStateStore();
        const sessionId = sessionState.sessionId;
        if (!sessionId || !roundId || isLoadingRoundVoteUpdate.value) return;
        await updateRoundVote(`/session/${sessionId}/rounds/${roundId}/vote`, { method: 'POST', data: { vote } });
    }

    async function search(keyword: string) {
        const sessionState = useSessionStateStore();
        const sessionId = sessionState.sessionId;
        if (sessionId && !isLoading.value) {
            await execute(`/session/${sessionId}/rounds?search=${keyword}`);
            roundsSearchResults.value = data.value ? data.value : ([] as RoundData[]);
        }

        return roundsSearchResults.value;
    }

    function clearSearchResults() {
        roundsSearchResults.value = [];
    }

    return {
        rounds,
        roundsSearchResults,
        fetch,
        search,
        setVote,
        clearSearchResults,
    };
});
